import { useEffect, useState } from "react";
import { useUserDetailsContext } from "@zenith/context/global-context/hooks";
import { PreferencesDTO, UsersApiService } from "@zenith/services/user";

const MODULE_FEATURE = "virtual_account_module";
const GENERATE_FEATURE = "generate_virtual_account";

export interface VirtualAccountFeatures {
  isLoaded: boolean;
  isModuleEnabled: boolean;
  isGenerateEnabled: boolean;
}

type FeatureMap = PreferencesDTO["features"];

const cache = new Map<string, Promise<FeatureMap>>();

const fetchFeatures = (userId: string): Promise<FeatureMap> => {
  const cached = cache.get(userId);
  if (cached) return cached;

  const request = UsersApiService.getPreferences(userId)
    .then((response) => {
      const body = response as PreferencesDTO & { data?: PreferencesDTO };
      return (body.data ?? body).features;
    })
    .catch((error) => {
      cache.delete(userId);
      throw error;
    });
  cache.set(userId, request);
  return request;
};

export function useVirtualAccountFeatures(): VirtualAccountFeatures {
  const { userDetailsContext } = useUserDetailsContext();
  const userId = userDetailsContext.id;
  const [features, setFeatures] = useState<FeatureMap>();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let isCurrent = true;

    fetchFeatures(String(userId))
      .then((result) => {
        if (isCurrent) setFeatures(result);
      })
      .catch((error) => console.error("Failed to fetch virtual account features:", error))
      .finally(() => {
        if (isCurrent) setIsLoaded(true);
      });

    return () => {
      isCurrent = false;
    };
  }, [userId]);

  return {
    isLoaded,
    isModuleEnabled: features?.[MODULE_FEATURE]?.enabled === true,
    isGenerateEnabled: features?.[GENERATE_FEATURE]?.enabled === true,
  };
}

export default useVirtualAccountFeatures;
